import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Role } from '@/application/permissions/permissionsTypes';
import { actionClass } from '@/presentation/components/common/actionButtons';
import { ModalPortal } from '@/presentation/components/common/ModalPortal';
import { useKeyEsc } from '@/presentation/hooks/useKeyEsc';
import { useMinWidthMd } from '@/presentation/hooks/useMinWidthMd';

export function RoleRowActions({
  role,
  atLimit,
  limitTitle,
  onEdit,
  onSimulate,
  onDuplicate,
  onDelete,
}: {
  role: Role;
  atLimit: boolean;
  limitTitle?: string;
  onEdit: () => void;
  onSimulate: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}) {
  const isDesktop = useMinWidthMd();
  if (isDesktop) {
    return (
      <DesktopRoleActions
        role={role}
        atLimit={atLimit}
        limitTitle={limitTitle}
        onEdit={onEdit}
        onSimulate={onSimulate}
        onDuplicate={onDuplicate}
        onDelete={onDelete}
      />
    );
  }
  return (
    <MobileRoleActions
      role={role}
      atLimit={atLimit}
      limitTitle={limitTitle}
      onEdit={onEdit}
      onSimulate={onSimulate}
      onDuplicate={onDuplicate}
      onDelete={onDelete}
    />
  );
}

function DesktopRoleActions({
  role,
  atLimit,
  limitTitle,
  onEdit,
  onSimulate,
  onDuplicate,
  onDelete,
}: {
  role: Role;
  atLimit: boolean;
  limitTitle?: string;
  onEdit: () => void;
  onSimulate: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex w-auto flex-wrap justify-end gap-1.5">
      {!role.isSystem && (
        <button type="button" onClick={onEdit} className={actionClass('primary')}>
          {t('permissions.editRole')}
        </button>
      )}
      <button type="button" onClick={onSimulate} className={actionClass('secondary')}>
        {t('permissions.simulate')}
      </button>
      <button type="button" onClick={onDuplicate} disabled={atLimit} title={limitTitle} className={actionClass('secondary')}>
        {t('permissions.duplicateRole')}
      </button>
      {!role.isSystem && (
        <button type="button" onClick={onDelete} className={actionClass('danger')}>
          {t('permissions.deleteRole')}
        </button>
      )}
    </div>
  );
}

function MobileRoleActions({
  role,
  atLimit,
  limitTitle,
  onEdit,
  onSimulate,
  onDuplicate,
  onDelete,
}: {
  role: Role;
  atLimit: boolean;
  limitTitle?: string;
  onEdit: () => void;
  onSimulate: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  useKeyEsc(close, open);

  const pair = 'panel-touch flex-1';
  const sheetRow =
    'flex min-h-12 w-full items-center justify-center px-4 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-50';

  if (role.isSystem) {
    return (
      <div className="flex w-full gap-2">
        <button type="button" onClick={onSimulate} className={`${pair} ${actionClass('secondary', 'page')}`}>
          {t('permissions.simulate')}
        </button>
        <button
          type="button"
          onClick={onDuplicate}
          disabled={atLimit}
          title={limitTitle}
          className={`${pair} ${actionClass('secondary', 'page')}`}
        >
          {t('permissions.duplicateRole')}
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="flex w-full gap-2">
        <button type="button" onClick={onEdit} className={`${pair} ${actionClass('primary', 'page')}`}>
          {t('permissions.editRole')}
        </button>
        <button
          type="button"
          aria-expanded={open}
          aria-haspopup="dialog"
          aria-label={t('permissions.moreAria', { name: role.name })}
          onClick={(event) => {
            event.stopPropagation();
            setOpen(true);
          }}
          className={`${pair} ${actionClass('secondary', 'page')}`}
        >
          {t('permissions.more')}
        </button>
      </div>

      {open && (
        <ModalPortal>
          <div
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
            role="presentation"
            data-testid="role-actions-backdrop"
            onClick={(event) => {
              event.stopPropagation();
              close();
            }}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby={`role-actions-title-${role.id}`}
              data-testid="role-actions-sheet"
              className="w-full max-w-sm overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-800"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="border-b border-gray-200 px-4 py-3 dark:border-gray-700">
                <h2
                  id={`role-actions-title-${role.id}`}
                  className="truncate text-sm font-semibold text-gray-900 dark:text-white"
                >
                  {role.name}
                </h2>
              </div>
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                <button
                  type="button"
                  onClick={() => {
                    close();
                    onSimulate();
                  }}
                  className={`${sheetRow} text-gray-800 hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-gray-700/60`}
                >
                  {t('permissions.simulate')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    close();
                    onDuplicate();
                  }}
                  disabled={atLimit}
                  title={limitTitle}
                  className={`${sheetRow} text-gray-800 hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-gray-700/60`}
                >
                  {t('permissions.duplicateRole')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    close();
                    onDelete();
                  }}
                  className={`${sheetRow} text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40`}
                >
                  {t('permissions.deleteRole')}
                </button>
              </div>
            </div>
          </div>
        </ModalPortal>
      )}
    </>
  );
}
